import React, { useCallback, useMemo } from 'react'
import { Form, Input, Typography, App } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AuthService from '@/libs/services/auth'
import AuthSubmitButton from '@/features/auth/components/AuthSubmitButton'
import {
  AuthForm,
  AuthFooterText,
  AuthTitleWrapper,
  AuthSubtitleText,
} from '@/features/auth/components/AuthFormStyles'

const { Title } = Typography

const ForgotPasswordView = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [isFormValid, setIsFormValid] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const { notification } = App.useApp()

  const onFinish = useCallback(
    async values => {
      setLoading(true)
      try {
        const response = await AuthService.resetPassword({
          email: values.email,
        })

        if (response.status === 204) {
          notification.success({
            title: t('common.successTitle'),
            description: t('auth.forgotPassword.success'),
            placement: 'topRight',
            duration: 3,
          })
          navigate('/auth/login', { replace: true })
        }
      } catch {
        notification.error({
          title: t('common.errorTitle'),
          description: t('auth.forgotPassword.error'),
          placement: 'topRight',
          duration: 3,
        })
        form.setFields([
          {
            name: 'email',
            errors: [t('auth.forgotPassword.error')],
          },
        ])
      } finally {
        setLoading(false)
      }
    },
    [navigate, form, notification, t]
  )

  const formRules = useMemo(
    () => ({
      email: [
        { required: true, message: t('auth.forgotPassword.emailRequired') },
        { type: 'email', message: t('auth.forgotPassword.emailInvalid') },
      ],
    }),
    [t]
  )

  // Add form validation check on field changes
  const onFieldsChange = () => {
    const values = form.getFieldsValue()
    setIsFormValid(values.email)
  }

  return (
    <>
      <AuthTitleWrapper>
        <Title level={3}>{t('auth.forgotPassword.title')}</Title>
      </AuthTitleWrapper>
      <AuthSubtitleText>{t('auth.forgotPassword.subtitle')}</AuthSubtitleText>

      <AuthForm
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        onFieldsChange={onFieldsChange}
      >
        <Form.Item name="email" label={t('auth.forgotPassword.email')} rules={formRules.email}>
          <Input
            prefix={<UserOutlined />}
            placeholder={t('auth.forgotPassword.email')}
            size="large"
          />
        </Form.Item>

        <Form.Item>
          <AuthSubmitButton
            type="primary"
            htmlType="submit"
            block
            loading={loading}
            disabled={!isFormValid}
          >
            {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.submit')}
          </AuthSubmitButton>
        </Form.Item>
      </AuthForm>

      <AuthFooterText>
        <Link to="/auth/login">{t('auth.forgotPassword.backToLogin')}</Link>
      </AuthFooterText>
    </>
  )
}

export default ForgotPasswordView
